import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const aiVoiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.aiVoiceoverVideoBasics,
    description: `Create or edit an AI-generated voiceover — drafting/generating narration from a script, choosing a voice, and the pause/reword/re-voice mechanics. Load when the video is driven by an AI voiceover (the default), or when editing one. For the shared overlay/coverage/scene-group mechanics, use ${SKILL_NAMES.voiceoverVideoBasics}.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# AI Voiceover Video Basics

Creating and editing an AI-generated voiceover. The shared scene-group mechanics —
overlay coverage, splits, reorders, re-timing, targeting — live in
\`${SKILL_NAMES.voiceoverVideoBasics}\`; this skill is the AI-specific part.

## Creating the voiceover
The voiceover is generated from a script. If the user hasn't given you one, analyze what
they've provided and use the questionnaire to ask clarifying questions (offer topics, etc).
Then write the script.
- \`${TOOL_NAMES.addVoiceoverSceneGroup}\` generates the AI voiceover audio and its synced
  transcript in one step from the **plain** script. Create a single voiceover scene
  group for the whole video (never one per scene), then build overlays over it.
- If the user requested a specific voice, find it with \`${TOOL_NAMES.searchVoices}\` first and pass
  its slug.

## Adding pauses
Pauses give narration room to breathe. The two-step procedure:
1. Generate the voiceover from the **plain** script (\`${TOOL_NAMES.addVoiceoverSceneGroup}\` —
   pause tags in the initial transcript would be read aloud).
2. Add pauses with \`${TOOL_NAMES.editAiVoiceover}\`, inserting \`<pause seconds="N"/>\` tokens in
   the replacement text.

Placement taste: after the opening hook, before a key point, ahead of the closing
line. Intentional and sparing — not between every sentence.

Once pauses exist they are transcript tokens like words: any word range you pass
(\`${TOOL_NAMES.addVoiceoverScene}\`, \`${TOOL_NAMES.splitSceneGroup}\`) must include the pause tokens
that fall inside it, written exactly as they appear.

## Pause token mechanics (${TOOL_NAMES.editAiVoiceover})
The transcript is one stream of tokens; a pause is just another token, written
\`<pause seconds="N"/>\` exactly as shown. \`find_phrase\`/\`replacement\` are literal
token find-and-replace: \`find_phrase\` must match one run of the transcript exactly
and uniquely — every word AND every \`<pause/>\` inside the run must appear in it (a
\`<pause/>\` in \`find_phrase\` matches any present pause; its N is ignored on match).
The matched run is replaced token-for-token; everything outside is untouched.
- **Add** a pause: include a \`<pause/>\` tag in the replacement.
- **Remove** one: match it in \`find_phrase\` and omit it from the replacement.
- **Move or resize** one: match it, then reposition it / change its N in the
  replacement. N is a whole number of seconds, 1-9; a fractional pause (from
  recorded audio, e.g. \`<pause seconds="2.3"/>\`) is kept by writing the nearest
  whole second.
- A \`<pause/>\` inside the run that's left out of \`find_phrase\` makes the match
  fail — include every one.

<example goal="add a word before an opening pause while keeping the pause">
  \`find_phrase\`: \`<pause seconds="1"/> Cupping is\`
  \`replacement\`: \`Hello. <pause seconds="1"/> Cupping is\`
</example>

## Rewording, re-voicing, speed
All three operations go through \`${TOOL_NAMES.editAiVoiceover}\`. A phrase replacement re-synthesizes only
the changed span; a voice or speed change re-synthesizes the whole voiceover.
To drop words, pass them as \`find_phrase\` with an empty \`replacement\`; a scene left with
no words is removed.
Overlays and scene timings re-align automatically — but re-check overlay text
afterwards, since on-screen text must stay within what the new words actually say.

## Fixing a mispronounced word
The voice reads the transcript's spelling. Re-submitting the same words, or only
changing punctuation or quotes around them, does not change how a word is said — never
do that and report it fixed. Two real options:
- **Respell it phonetically** in the transcript with \`${TOOL_NAMES.editAiVoiceover}\` (e.g.
  \`find_phrase\` \`with bolg\` → \`replacement\` \`with bollg\`), or split it into
  syllables when a word is misread as initials. Only the transcript changes: text you
  set with \`${TOOL_NAMES.setText}\` keeps its correct spelling, but animated captions are
  drawn from the transcript and will show the respelling. When captions are on, tell
  the user that tradeoff. Later word ranges must use the respelled token as it now
  appears.
- **The editor's pronunciation fix**, which you can't apply yourself but the user can:
  select the word in the transcript and choose "Fix pronunciation" (optionally
  "Apply override to all instances" to save it to the team's pronunciation library for
  future voiceovers). The transcript and captions keep the correct spelling. Point the
  user here when captions matter, or when a respelling didn't land after one retry.`;
    },
};
